<template>
  <div class="product">
    <div class="product__images">
      <WishlistButton class="wishlist-button" :product-id="product.id" />
      <a :href="product.onlineStoreUrl" class="product__image-link">
        <IntersectionObserver
          v-for="image in images"
          v-slot="{ intersected }"
          :key="image.id"
          class="picture-container"
        >
          <Picture
            :images="image"
            :intersected="intersected"
            height="308"
            width="205"
          />
        </IntersectionObserver>
      </a>
    </div>
    <div class="product__info">
      <a :href="product.onlineStoreUrl">
        <h2 class="product__title">
          {{ product.title }}
        </h2>
      </a>
      <button class="add-to-bag__button" @click="handleAddToBag">
        <template v-if="showAddToBag">
          Add to bag |
          {{ $filters.toCurrency(price.amount, price.currencyCode) }}
          <template v-if="isOnSale">
            <span class="product__price-onsale">{{
              $filters.toCurrency(
                originalPrice.amount,
                originalPrice.currencyCode
              )
            }}</span>
          </template>
        </template>
        <template v-else> Notify me </template>
      </button>
      <div v-if="isOnSale" class="product__onsale">SALE</div>
    </div>
  </div>
</template>

<script>
import Picture from "@/components/Picture.vue";
import WishlistButton from "@/components/WishlistButton.vue";
import { unwrapNodes } from "@/helpers/graphql";
import { mapActions } from "vuex";
import IntersectionObserver from "@/components/IntersectionObserver.vue";

export default {
  name: "ProductTile",
  components: {
    Picture,
    WishlistButton,
    IntersectionObserver,
  },
  inject: ["mq"],
  props: {
    product: {
      type: Object,
      required: true,
    },
    showOnePhoto: {
      type: Boolean,
      default: false,
    },
    flatLays: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    images() {
      const flatLayImages =
        unwrapNodes(this.product.flatlays?.references?.edges) || [];

      if (this.flatLays && flatLayImages.length > 0) {
        return flatLayImages.map((flatLay) => {
          return {
            ...flatLay.image,
          };
        });
      }

      const images = unwrapNodes(this.product.images.edges);
      if ((this.showOnePhoto || this.mq.mdPlus) && images.length > 0) {
        return [images[0]];
      }
      return images;
    },
    price() {
      return this.product.priceRange.minVariantPrice;
    },
    originalPrice() {
      return this.product.compareAtPriceRange.minVariantPrice;
    },
    isOnSale() {
      return (
        parseFloat(this.originalPrice.amount) > parseFloat(this.price.amount)
      );
    },
    showAddToBag() {
      const hasMultipleVariants =
        unwrapNodes(this.product.variants.edges).length > 1;
      const isAvailableForSale = this.product.availableForSale;

      if (!hasMultipleVariants && !isAvailableForSale) {
        return false;
      }

      return true;
    },
  },
  methods: {
    ...mapActions("add-to-bag", ["openAddToBag"]),
    handleAddToBag() {
      this.openAddToBag(this.product);
    },
  },
};
</script>

<style lang="scss" scoped>
.product {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__onsale {
    color: #070707;
    font-size: 11px;
    margin: 5px 0 0 0;
    opacity: 0.3;
  }

  &__price-onsale {
    text-decoration: line-through;
  }
}

.product__info {
  padding: 10px 10px 40px 10px;

  @include respond-above(md) {
    padding: 10px 10px 40px 0;
  }

  a {
    text-decoration: none;
    color: $color-dark;
  }
}

.product__images {
  display: flex;
  overflow-x: scroll;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  white-space: nowrap;
  line-height: 0;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }

  & .picture-container {
    scroll-snap-align: center;
    display: inline-block;
    position: relative;
    width: 100%;
  }
}

.product__image-link {
  display: block;
  width: 100%;
}

.product__title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.add-to-bag__button {
  font-size: 9px;
  outline: none;
  background: none;
  border: none;
  padding: 0;
  text-transform: uppercase;
  font-weight: 600;
}

.wishlist-button {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
}
</style>
